import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, LinearProgress, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import PageLayout from '../shell/PageLayout';
import TripPlayback from '../trips/TripPlayback';
import { request, useOperations } from './OperationsProvider';
import { formatDateTime } from '../common/util/datetime';

// What one truck did over a span of days, played back.
//
// Trip replay answers "where did this consignment go". This answers the other question, the one a
// haulier asks and a plant asks when a customer disputes an hour: where was this lorry on Tuesday.
// A trip is a thing the plant opened and closed; a day is not, and a truck that ran four trips and
// sat idle between them has a day that no single trip describes.
//
// The fixes come from the vehicle's own history rather than any trip's, so a period with no trip at
// all still replays - which is exactly the period somebody is usually asking about.
const midnight = (days) => {
  const at = new Date();
  at.setDate(at.getDate() - days);
  at.setHours(0, 0, 0, 0);
  return at;
};
const local = (at) => new Date(at.getTime() - at.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

const VehicleReplayPage = () => {
  const { data, trips } = useOperations();
  const [vehicle, setVehicle] = useState(null);
  const [from, setFrom] = useState(() => local(midnight(1)));
  const [to, setTo] = useState(() => local(midnight(0)));
  const [state, setState] = useState({ items: [], total: 0, thinned: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Trucks that have actually run, most recent first, then the rest alphabetically.
  //
  // An alphabetical list of the whole fleet puts whichever plate begins with A at the top, and that
  // truck may not have moved in a month. Somebody opening replay is nearly always asking about a
  // lorry that has just run, so the ones with trips to replay come first and carry the date of their
  // last one; a truck with nothing recorded is still selectable, just not in the way.
  const vehicles = useMemo(() => {
    const lastRun = new Map();
    (trips || []).forEach((t) => {
      const at = Date.parse(t.gateIn || t.createdAt || '');
      if (!Number.isFinite(at)) return;
      if (!lastRun.has(t.vehicleId) || at > lastRun.get(t.vehicleId)) lastRun.set(t.vehicleId, at);
    });
    return (data.vehicles || []).slice()
      .map((v) => ({ ...v, lastRun: lastRun.get(v.id) || 0 }))
      .sort((a, b) => (b.lastRun - a.lastRun) || a.name.localeCompare(b.name));
  }, [data.vehicles, trips]);

  useEffect(() => {
    if (!vehicle) { setState({ items: [], total: 0, thinned: false }); return undefined; }
    const controller = new AbortController();
    setLoading(true); setError('');
    const span = `vehicleId=${vehicle.id}&from=${encodeURIComponent(new Date(from).toISOString())}&to=${encodeURIComponent(new Date(to).toISOString())}`;
    request(`/api/operations/samples?${span}`, 'GET', null, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setState(result); })
      .catch((e) => { if (!controller.signal.aborted) { setError(e.message); setState({ items: [], total: 0, thinned: false }); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [vehicle?.id, from, to]);

  const fixes = state.items || [];
  const span = fixes.length > 1
    ? `${formatDateTime(fixes[0].fixTime)} to ${formatDateTime(fixes[fixes.length - 1].fixTime)}`
    : '';

  return (
    <PageLayout title="Vehicle replay" subtitle="Where one truck was, through a period you choose.">
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
          <Autocomplete
            sx={{ minWidth: 240 }}
            options={vehicles}
            getOptionLabel={(v) => v.name || ''}
            groupBy={(v) => (v.lastRun ? 'Recently run' : 'No trips recorded')}
            renderOption={(props, v) => (
              <li {...props} key={v.id}>
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{v.name}</Typography>
                  {!!v.lastRun && (
                    <Typography variant="caption" color="text.secondary">
                      {`last trip ${new Date(v.lastRun).toLocaleDateString()}`}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={vehicle}
            onChange={(event, chosen) => setVehicle(chosen)}
            renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="Choose a truck" />}
          />
          <TextField type="datetime-local" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="datetime-local" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField
            select
            label="Period"
            value=""
            sx={{ minWidth: 150 }}
            onChange={(e) => { setFrom(local(midnight(Number(e.target.value)))); setTo(local(midnight(0))); }}
          >
            <MenuItem value="1">Yesterday</MenuItem>
            <MenuItem value="3">Last 3 days</MenuItem>
            <MenuItem value="7">Last 7 days</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!vehicle && <Alert severity="info">Choose a truck to replay.</Alert>}
      {vehicle && !loading && !error && fixes.length < 2 && (
        <Alert severity="info">{`${vehicle.name} recorded nothing in this period. It may not have been carrying a unit, or it may not have moved.`}</Alert>
      )}
      {vehicle && fixes.length > 1 && (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {`${vehicle.name} · ${span}`}
            </Typography>
          </Box>
          <TripPlayback trail={fixes} label={vehicle.name} height={420} />
          {state.thinned && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {`Showing ${fixes.length.toLocaleString('en-IN')} of ${(state.total || 0).toLocaleString('en-IN')} recorded fixes over this period. The trail is thinned evenly, so brief halts between the fixes shown may not appear. Narrow the dates for a complete trace.`}
            </Alert>
          )}
        </>
      )}
    </PageLayout>
  );
};

export default VehicleReplayPage;
